import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { Link } from "@msuite/ui/link";
import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { StateTag, Tag } from "@msuite/ui/tag";
import { getAbsenceCategoryTranslationKey } from "@msuite/utils/absences";
import { getColorByString } from "@msuite/utils/colors";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import { getFullName } from "@msuite/utils/users";
import Notes from "@/components/notes";
import { useSubstitutionPlan } from "@/hooks/use-substitution-plan";
import type { SubstitutionPlanBasePanelProps } from "../shared";
import { SubstitutionGeneralSummary } from "./substitution-general-summary";

export function SubstitutionGeneral({ substitutionPlanId }: SubstitutionPlanBasePanelProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { substitutionPlan } = useSubstitutionPlan(substitutionPlanId);

	/** Render */
	return (
		<Panels>
			<Panels.Panel flex={1}>
				<ContentCard
					icon={icon("Info")}
					title={t("general.information")}
				>
					<DataGrid>
						<DataGrid.Row>
							<DataGrid.Label>{t("personnel.employees.employee")}</DataGrid.Label>
							<DataGrid.Value>
								<Link href={substitutionPlan ? `/users/${substitutionPlan.userId}` : null}>
									{getFullName(substitutionPlan)}
								</Link>
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("general.type")}</DataGrid.Label>
							<DataGrid.Value>
								{substitutionPlan && (
									<Tag
										variant="plain"
										size="md"
										color={getColorByString(substitutionPlan.category)}
									>
										{t(getAbsenceCategoryTranslationKey(substitutionPlan.category))}
									</Tag>
								)}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("dates.start_date")}</DataGrid.Label>
							<DataGrid.Value>
								{substitutionPlan
									? moment(substitutionPlan.startDate).dateFormat()
									: guardString(null)}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("dates.end_date")}</DataGrid.Label>
							<DataGrid.Value>
								{substitutionPlan
									? moment(substitutionPlan.endDate).dateFormat()
									: guardString(null)}
							</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("general.state")}</DataGrid.Label>
							<DataGrid.Value>
								<StateTag
									state={substitutionPlan?.state}
									size="md"
								/>
							</DataGrid.Value>
						</DataGrid.Row>
					</DataGrid>
				</ContentCard>
			</Panels.Panel>
			<Panels.Panel flex={2}>
				<ContentCard
					icon={icon("Users")}
					title={t("substitutions.summary")}
				>
					{substitutionPlan && (
						<SubstitutionGeneralSummary absenceId={substitutionPlan.absenceId} />
					)}
				</ContentCard>
				<Notes
					notesTable="substitutionPlanNotes"
					identifier={substitutionPlanId}
				/>
			</Panels.Panel>
		</Panels>
	);
}
